<template>
  <popup-dialog
    :visible="visible"
    :title="$t('actions.shareModel')"
    :close-button-visible="true"
    @close="close"
  >
    <div class="share-popup">
      <div v-if="usersModelIsNotSharedWith.length > 0" class="mb-4">
        <div class="label">{{ $t("headings.chooseFromUsers") }}</div>
        <users-picker
          :users="usersModelIsNotSharedWith"
          :selected-user-ids="selectedKnownUserIds"
          @check-user="addEmail($event.email)"
          @uncheck-user="removeEmail($event.email)"
        />
      </div>

      <input-email-list
        ref="emailInput"
        class="mt-2"
        :emails="emails"
        :autocomplete-options="knownUsersAutocompleteOptions"
        @add="emails.push($event)"
      />

      <div class="well">
        <label class="label">{{ $t("headings.receivers") }}</label>
        <form id="share-popup-form" class="mb-4" @submit.prevent="onSubmit">
          <div class="share-popup__list">
            <div
              v-for="(email, index) in emails"
              :key="index"
              class="share-popup__entry"
            >
              <div>
                <span>{{ emails[index] }}</span>
                <!-- <input-text v-model:value="emails[index]" type="email" /> -->
              </div>
              <span>{{
                knownUsersByEmail[email]
                  ? `${knownUsersByEmail[email].name}`
                  : ""
              }}</span>
              <button
                :title="$t('actions.removeEmail')"
                class="button-icon"
                type="button"
                @click="removeEmail(email)"
              >
                <lucide-icon name="trash-2" />
              </button>
            </div>
          </div>

          <div v-show="emails.length == 0">
            {{ $t("texts.noShareReceiversYet") }}
          </div>
        </form>

        <button
          class="button"
          type="submit"
          form="share-popup-form"
          :disabled="loading || emails.length == 0"
        >
          <span v-show="!loading">{{ $t("actions.share") }}</span>
          <span v-show="loading">{{ $t("messages.isBeeingShared") }}</span>
        </button>
      </div>
    </div>
  </popup-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";

import type { User } from "@/api/User";
import type { Model } from "@/api/Model";

import PopupDialog from "@core/ui/PopupDialog.vue";
import InputEmailList from "@core/input/InputEmailList.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import UsersPicker from "../common/UsersPicker.vue";

import { useUser } from "@/api/user.store";
import { useModels } from "@/api/models.store";

const props = withDefaults(defineProps<{
  visible: boolean;
  model: Model | null;
}>(), {
  visible: false,
  model: null
});

const emit = defineEmits<{
  (event: "close"): void;
}>();

const { getKnownUsers } = useUser();
const { shareModel } = useModels();
const emailInput = ref<null | InstanceType<typeof InputEmailList>>(null);

const emails = ref<string[]>([]);
const knownUsers = ref<User[]>([]);
const loading = ref(false);


const emailsModelIsSharedWith = computed(() => {
  const shares = props.model?.shares || [];
  return shares.map((share) => share.user.email);
});

const usersModelIsNotSharedWith = computed(() => {
  return knownUsers.value.filter(
    (user) => !emailsModelIsSharedWith.value.includes(user.email)
  );
});

const knownUsersAutocompleteOptions = computed(() => {
  return usersModelIsNotSharedWith.value
    .filter((user) => !emails.value.includes(user.email))
    .map((user) => ({
      label: `${user.name}`,
      value: `${user.email}`
    }));
});

const knownUsersByEmail = computed(() => {
  return knownUsers.value.reduce((list, user) => {
    list[user.email] = user;
    return list;
  }, {} as { [email: string]: User });
});


const selectedKnownUserIds = computed(() => {
  return knownUsers.value
    .filter((u) => emails.value.includes(u.email))
    .map((u) => u.id);
});

watch(() => props.visible, (visible) => {
  if (visible) {
    loadKnownUsers();
  }
}, { immediate: true });

async function loadKnownUsers() {
  try {
    knownUsers.value = await getKnownUsers();
  } catch (_error) {
    const error = _error as Error;
    console.warn(error.message);
  }
}

function addEmail(email: string) {
  emails.value.push(email);
}

function removeEmail(email: string) {
  const index = emails.value.findIndex((e) => e == email);
  emails.value.splice(index, 1);
}

function onSubmit() {
  if (emailInput.value) {
    if (emailInput.value.submit()) {
      createNewShares();
    }
  }
}
async function createNewShares() {
  loading.value = true;
  try {
    if (!props.model) {
      throw new Error("Model is not set");
    }
    const flatEmails = emails.value.map((e) => ({ email: e }));
    await shareModel(props.model.id, flatEmails);
    emails.value = [];
    close();
  } catch (error) {
    console.log(error);
  } finally {
    loading.value = false;
  }
}

function close() {
  emit("close");
}
</script>

<style lang="scss">
.share-popup {
  padding: 2rem;

  &__list {
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__entry {
    display: grid;
    grid-template-columns: 1fr 1fr 3rem;
    gap: 0.5rem;
    align-items: center;
    background-color: var(--color-background-close);
    padding: 0 1rem;
    border-radius: 5px;
  }
}
</style>
